// ── Request / response shapes ─────────────────────────────────────────────────

export interface TextGenerationRequest {
  readonly userPrompt: string;
  readonly systemPrompt?: string;
  readonly maxTokens?: number;
  readonly temperature?: number;
}

export interface TextGenerationResponse {
  readonly text: string;
  readonly modelId: string;
}

export interface ImageGenerationRequest {
  readonly prompt: string;
  readonly width?: number;
  readonly height?: number;
}

export interface ImageGenerationResponse {
  /** Base-64 data URL (`data:image/png;base64,…`) ready for `<img src>`. */
  readonly dataUrl: string;
  readonly modelId: string;
}

// ── Error type ────────────────────────────────────────────────────────────────

/** Thrown when the active client has no API keys configured. */
export class LLMUnavailableError extends Error {
  constructor(message = "LLM client is not configured") {
    super(message);
    this.name = "LLMUnavailableError";
  }
}

// ── Interface ─────────────────────────────────────────────────────────────────

export interface LLMClient {
  /** True when API keys are present and real calls can be made. */
  readonly isConfigured: boolean;

  /** Generate text — character backgrounds, barks, journal entries, etc. */
  generateText(request: TextGenerationRequest): Promise<TextGenerationResponse>;

  /** Generate an image — character portraits, room art, etc. */
  generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResponse>;
}

// ── NullLLMClient — offline fallback ─────────────────────────────────────────

/**
 * No-op client used until the player supplies API keys (L02).
 * Every call rejects with `LLMUnavailableError` so callers can branch on
 * `isConfigured` or catch the error to show their offline fallback.
 */
export class NullLLMClient implements LLMClient {
  readonly isConfigured = false;

  generateText(_request: TextGenerationRequest): Promise<TextGenerationResponse> {
    return Promise.reject(new LLMUnavailableError());
  }

  generateImage(_request: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    return Promise.reject(new LLMUnavailableError());
  }
}

// ── Module singleton ──────────────────────────────────────────────────────────

let _client: LLMClient = new NullLLMClient();

/** Returns the currently active LLM client (NullLLMClient by default). */
export function getLLMClient(): LLMClient {
  return _client;
}

/**
 * Swap the active client.
 * Called by the settings integration (L02) once the user saves their API keys.
 */
export function setLLMClient(client: LLMClient): void {
  _client = client;
}
